import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import { isStaleAssetFailure } from '../../../utils/assetPolicy';
import { recoverFromStaleAssets, showAssetRecoveryFallback } from '../../../utils/assetRecovery';
import { logError } from '../../../utils/errorLogger';
import './ErrorBoundary.css';

/**
 * React Error Boundary Class Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of crashing.
 */
class ReactErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error) {
    const isChunkError = isStaleAssetFailure({
      message: error && error.message,
      name: error && error.name,
    });

    return {
      hasError: true,
      error,
      isChunkError,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log error using centralized error logger
    logError(error, errorInfo, {
      component: 'ReactErrorBoundary',
      boundary: true,
    });

    // Log error details to state for display
    this.setState({
      errorInfo,
    });

    if (isStaleAssetFailure({ message: error && error.message, name: error && error.name })) {
      recoverFromStaleAssets({
        sessionStorage: window.sessionStorage,
        caches: window.caches,
        getRegistrations: () =>
          'serviceWorker' in navigator
            ? navigator.serviceWorker.getRegistrations()
            : Promise.resolve([]),
        reload: () => window.location.reload(),
        showFallback: () => showAssetRecoveryFallback(document),
        hostname: window.location.hostname,
      });
    }
  }

  handleReset = () => {
    if (this.state.isChunkError) {
      recoverFromStaleAssets({
        sessionStorage: window.sessionStorage,
        caches: window.caches,
        getRegistrations: () =>
          'serviceWorker' in navigator
            ? navigator.serviceWorker.getRegistrations()
            : Promise.resolve([]),
        reload: () => window.location.reload(),
        showFallback: () => showAssetRecoveryFallback(document),
        hostname: window.location.hostname,
      });
      return;
    }
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isChunkError: false,
    });
  };

  render() {
    if (this.state.hasError) {
      // Render custom fallback UI
      return (
        <ErrorBoundary
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleReset}
          isChunkError={this.state.isChunkError}
        />
      );
    }

    return this.props.children;
  }
}

export default ReactErrorBoundary;

